import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { IconPlus } from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import templateSetService from '../../../services/templateSetService';
import TemplateSetFormDrawer from '../../../components/admin/TemplateSetFormDrawer';
import TemplateSetTable from '../../../components/templates/TemplateSetTable';
import { useTranslation } from '../../../i18n/hooks/useTranslation';
import ConfirmDialog from '../../../components/common/ConfirmDialog';

export default function AdminTemplatesPage() {
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [deleteModal, setDeleteModal] = useState({
        opened: false,
        templateSet: null
    });
    const [formDrawer, setFormDrawer] = useState({
        opened: false,
        templateSet: null
    });
    const [tableKey, setTableKey] = useState(0); // Key to force table refresh

    // Load template set data when ID is in URL
    useEffect(() => {
        const loadTemplateSetForEdit = async () => {
            if (id) {
                try {
                    const response = await templateSetService.getTemplateSetById(id);
                    setFormDrawer({
                        opened: true,
                        templateSet: response.data
                    });
                } catch (error) {
                    notifications.show({
                        title: t('common.error'),
                        message: t('templateSets.failedToLoad'),
                        color: 'red'
                    });
                    navigate('/admin/template-sets');
                }
            } else if (location.pathname === '/admin/template-sets/new') {
                setFormDrawer({
                    opened: true,
                    templateSet: null
                });
            }
        };

        loadTemplateSetForEdit();
    }, [id, location.pathname, navigate]);

    // Handle delete
    const handleDeleteClick = (templateSet) => {
        setDeleteModal({
            opened: true,
            templateSet
        });
    };

    const handleDeleteConfirm = async () => {
        try {
            await templateSetService.deleteTemplateSet(deleteModal.templateSet.id);

            notifications.show({
                title: t('common.success'),
                message: t('templateSets.templateSetDeleted'),
                color: 'green'
            });

            setDeleteModal({ opened: false, templateSet: null });
            setTableKey(prev => prev + 1); // Refresh table
        } catch (error) {
            notifications.show({
                title: t('common.error'),
                message: t('templateSets.failedToDelete') || error.message,
                color: 'red'
            });
        }
    };

    // Handle edit
    const handleEditClick = (templateSet) => {
        navigate(`/admin/template-sets/${templateSet.id}/edit`);
    };

    // Handle copy
    const [copyingId, setCopyingId] = useState(null);
    const handleCopyClick = async (templateSet) => {
        setCopyingId(templateSet.id);
        try {
            await templateSetService.duplicateTemplateSet(templateSet.id);

            notifications.show({
                title: t('common.success'),
                message: t('templateSets.templateSetCopied') || 'Template set copied successfully',
                color: 'green'
            });

            setTableKey(prev => prev + 1); // Refresh table
        } catch (error) {
            notifications.show({
                title: t('common.error'),
                message: t('templateSets.failedToCopy') || error.message,
                color: 'red'
            });
        } finally {
            setCopyingId(null);
        }
    };

    // Handle add new
    const handleAddNew = () => {
        navigate('/admin/template-sets/new');
    };

    // Keyboard shortcut: Ctrl/Cmd + Shift + K -> open New Template Set drawer
    useEffect(() => {
        const handleKeyDown = (e) => {
            // Ignore when typing in inputs or textareas
            const tagName = e.target.tagName;
            if (tagName === 'INPUT' || tagName === 'TEXTAREA') return;

            if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'k' || e.key === 'K')) {
                e.preventDefault();
                handleAddNew();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleAddNew]);

    // Handle form close
    const handleFormClose = () => {
        setFormDrawer({ opened: false, templateSet: null });
        navigate('/admin/template-sets');
    };

    // Called when the inline editor opens — close the drawer without navigating so the
    // component stays mounted and the async R2 content fetch can complete
    const handleEditorOpen = () => {
        setFormDrawer(prev => ({ ...prev, opened: false }));
    };

    // Handle form success
    const handleFormSuccess = () => {
        setFormDrawer({ opened: false, templateSet: null });
        setTableKey(prev => prev + 1); // Refresh table
        navigate('/admin/template-sets');
    };

    return (
        <div className="w-full font-hind space-y-5 py-2 sm:py-4">
            {/* Header */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <h1 className="text-xl sm:text-2xl font-bold text-zinc-900 dark:text-zinc-100 tracking-tight">{t('templateSets.templateSetManagement')}</h1>
                    <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">{t('templateSets.managementSubtitle')}</p>
                </div>
                <button
                    onClick={handleAddNew}
                    className="flex items-center gap-1.5 px-4 py-2.5 bg-primary hover:bg-primary/90 text-white text-[13px] font-semibold rounded-lg transition-colors flex-shrink-0"
                >
                    <IconPlus size={16} stroke={2} />
                    {t('templateSets.addNewTemplateSet')}
                </button>
            </div>

            {/* Template Set Table Component */}
            <TemplateSetTable
                key={tableKey}
                isAdmin={true}
                showActions={true}
                onEdit={handleEditClick}
                onCopy={handleCopyClick}
                copyingId={copyingId}
                onDelete={handleDeleteClick}
            />

            {/* Delete Confirmation Modal */}
            <ConfirmDialog
                open={deleteModal.opened}
                onClose={() => setDeleteModal({ opened: false, templateSet: null })}
                onConfirm={handleDeleteConfirm}
                title={t('templateSets.deleteTemplateSet')}
                description={`${t('templateSets.confirmDelete')} "${deleteModal.templateSet?.name}"? ${t('templateSets.deleteWarning')}`}
            />

            {/* Template Set Form Drawer */}
            <TemplateSetFormDrawer
                opened={formDrawer.opened}
                onClose={handleFormClose}
                onEditorOpen={handleEditorOpen}
                templateSet={formDrawer.templateSet}
                onSuccess={handleFormSuccess}
            />
        </div>
    );
}
